import type { TFunction } from "i18next";
import { format } from "date-fns";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import {
  EXPEDITION_TYPES,
  FileAction,
  getVisibleFileTypes,
  POST_EXPEDITION_TYPES,
  PRE_EXPEDITION_TYPES,
  sortDocumentsByPriority,
} from "@/constants/fileCrud";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";

export type DrawerT = TFunction<["boExpeditions", "common"]>;

export const formatDate = (date: string | null | undefined, pattern = "dd/MM/yyyy") =>
  date ? format(new Date(date), pattern) : null;

export const getFullName = (firstName?: string | null, lastName?: string | null) =>
  [firstName, lastName].filter(Boolean).join(" ");

export const getVolumeLabel = (
  expedition: GetOneExpeditionBoResponseDto,
  t: DrawerT,
): string | null => {
  const quotation = expedition.quotation;
  if (!quotation) return null;

  const items = quotation.deliveryContent?.length
    ? quotation.deliveryContent.map((c) => ({ qty: c.numberOfBottles, volume: c.volume }))
    : quotation.quotationProducts?.map((p) => ({ qty: p.bottleQuantity, volume: p.volume })) ?? [];

  const nbBottles = items.reduce((acc, item) => acc + item.qty, 0);
  const totalVolume = items.reduce((acc, item) => acc + item.qty * item.volume, 0);

  if (!nbBottles && !totalVolume) return null;
  return t("expeditionDrawerDetails.volumeValue", { bottles: nbBottles, liters: totalVolume });
};

export const getSortedDocuments = (expedition: GetOneExpeditionBoResponseDto) => {
  const readableFileTypes = getVisibleFileTypes(FileAction.BO_DETAIL_EXPEDITION);
  const readableDocuments = (expedition.expeditionDocuments?.documents ?? []).filter((document) =>
    readableFileTypes.includes(document.type as FileTypeEnum),
  );
  return sortDocumentsByPriority(readableDocuments);
};

const DOCUMENT_SECTIONS = [
  { key: "preExpedition", types: PRE_EXPEDITION_TYPES },
  { key: "expedition", types: EXPEDITION_TYPES },
  { key: "postExpedition", types: POST_EXPEDITION_TYPES },
] as const;

export const getDocumentSections = (expedition: GetOneExpeditionBoResponseDto) => {
  const documents = getSortedDocuments(expedition);
  const sectionedTypes: readonly FileTypeEnum[] = DOCUMENT_SECTIONS.flatMap(({ types }) => types);
  const sections = DOCUMENT_SECTIONS.map(({ key, types }) => ({
    key,
    documents: documents.filter((document) =>
      (types as readonly FileTypeEnum[]).includes(document.type as FileTypeEnum),
    ),
  }));
  const others = documents.filter(
    (document) => !sectionedTypes.includes(document.type as FileTypeEnum),
  );
  return [...sections, { key: "other" as const, documents: others }].filter(
    (section) => section.documents.length > 0,
  );
};
